import { useState } from "react";
import { Link } from "react-router-dom";
import { Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useLoad, act } from "@/lib/hooks";
import { api } from "@/lib/api";
import { NativeSelect } from "@/components/common";
import { SERVICE_LINES } from "@/lib/serviceLines";

function PlanDialog({ inv, plan, cfg, onClose, onDone }) {
  const [rows, setRows] = useState(plan.lines);
  const set = (i, k, v) => setRows(rows.map((r, x) => (x === i ? { ...r, [k]: v, ...(k === "service_line" ? { job_type: null } : {}) } : r)));
  const ok = rows.every((r) => r.service_line && (!(cfg?.job_types?.[r.service_line] || []).length || r.job_type));
  const save = async () => {
    const r = await act(() => api.post(`/tenant/invoices/${inv.id}/job-order`, { lines: rows.map(({ index, service_line, job_type }) => ({ index, service_line, job_type })) }));
    if (r) { onClose(); onDone(r); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="job-order-dialog">
        <DialogHeader><DialogTitle>Create job order from {inv.number} v{inv.version || 1}</DialogTitle>
          <DialogDescription>One job per invoice line, with the customer, description and quantity copied from the latest version. Discount, surcharge and rounding lines are skipped.</DialogDescription></DialogHeader>
        <div className="max-h-[55vh] space-y-3 overflow-auto">{rows.map((r, i) => (
          <div key={r.index} className="grid gap-2 rounded border border-slate-200 p-3 text-sm sm:grid-cols-3" data-testid={`job-order-line-${i}`}>
            <div className="min-w-0 sm:col-span-3"><b>{r.description}</b> · qty {r.quantity}</div>
            <NativeSelect testId={`job-order-sl-${i}`} value={r.service_line || ""} onChange={(v) => set(i, "service_line", v)} placeholder="Service line" options={SERVICE_LINES} />
            <NativeSelect testId={`job-order-jt-${i}`} value={r.job_type || ""} onChange={(v) => set(i, "job_type", v)} placeholder="Job type"
              options={(cfg?.job_types?.[r.service_line] || []).map((j) => ({ value: j.code, label: j.name || j.label || j.code }))} />
          </div>))}</div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="job-order-confirm" disabled={!ok} onClick={save} className="bg-[#13294B]">Create job order</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function JobOrderPanel({ inv, canCreate, onDone }) {
  const [plan, reload] = useLoad(`/tenant/invoices/${inv.id}/job-order`);
  const [cfg] = useLoad("/estimating/config");
  const [open, setOpen] = useState(false);
  if (!plan) return null;
  if (plan.existing.length) return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="invoice-job-links"><span className="text-slate-500">Job order:</span>
      {plan.existing.map((j) => <Link key={j.id} data-testid={`invoice-job-link-${j.number}`} className="font-medium text-[#13294B] underline" to={`/app/jobs/${j.id}`}>{j.number}</Link>)}</div>
  );
  if (!canCreate || inv.status === "cancelled" || !plan.lines.length) return null;
  return (
    <>
      <Button variant="outline" data-testid="invoice-job-order-button" onClick={() => setOpen(true)}><Hammer className="mr-1 h-4 w-4" />Create job order</Button>
      {open && <PlanDialog inv={inv} plan={plan} cfg={cfg} onClose={() => setOpen(false)} onDone={() => { reload(); onDone(); }} />}
    </>
  );
}
